import {
  Alert,
  Card,
  FormField,
  Input,
  RadioCardGroup,
  type RadioCardOption,
} from '@playerbase/ui';
import {
  DEFAULT_PLAYTEST_RECRUITMENT_BUTTON_LABEL,
  PLAYTEST_RECRUITMENT_BUTTON_LABEL_MAX,
  PLAYTEST_RECRUITMENT_DESCRIPTION_MAX,
  PLAYTEST_RECRUITMENT_TITLE_MAX,
  type PlaytestRecruitmentFrequency,
} from '@playerbase/types';
import type { DiscordGuildChannel } from '@playerbase/types';

import { DiscordChannelMultiSelect } from '../../game-settings/discord/discord-channel-multi-select.js';

interface RecruitmentFormCardProps {
  title: string;
  description: string;
  buttonLabel: string;
  repostFrequency: PlaytestRecruitmentFrequency;
  channelIds: string[];
  channels: DiscordGuildChannel[];
  channelsLoading: boolean;
  hasConnection: boolean;
  onChange: (
    patch: Partial<{
      title: string;
      description: string;
      buttonLabel: string;
      repostFrequency: PlaytestRecruitmentFrequency;
      channelIds: string[];
    }>,
  ) => void;
}

const FIELD_LABEL_CLASS =
  'font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted';

// Same 4-up RadioCardGroup the feedback-form routing card uses, so the
// two surfaces feel identical.
const REPOST_OPTIONS: ReadonlyArray<RadioCardOption> = [
  { value: 'never', label: 'Never' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Title / description / button / repost / channels. Matches the
// feedback-form recruit card visually - same kind of channel
// multi-select, same kind of inputs - so the surface feels consistent.
export function RecruitmentFormCard({
  title,
  description,
  buttonLabel,
  repostFrequency,
  channelIds,
  channels,
  channelsLoading,
  hasConnection,
  onChange,
}: RecruitmentFormCardProps): JSX.Element {
  return (
    <Card>
      <Card.Header>
        <Card.Title>Recruitment message</Card.Title>
        <Card.Description>
          The bot posts this in the channels you pick. Tapping the button
          opens a DM with the player&rsquo;s pairing code.
        </Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-5">
        {!hasConnection ? (
          <Alert tone="warning">
            Connect this game to Discord on the Game settings &rarr; Discord
            tab before configuring the recruitment message.
          </Alert>
        ) : null}

        <FormField>
          <FormField.Label className={FIELD_LABEL_CLASS}>Title</FormField.Label>
          <FormField.Control>
            <Input
              value={title}
              onChange={(e) => onChange({ title: e.target.value })}
              maxLength={PLAYTEST_RECRUITMENT_TITLE_MAX}
              placeholder="Join the closed alpha"
            />
          </FormField.Control>
          <FormField.HelpText>Shown as the embed title in the post.</FormField.HelpText>
        </FormField>

        <FormField>
          <FormField.Label className={FIELD_LABEL_CLASS}>Description</FormField.Label>
          <FormField.Control>
            <textarea
              value={description}
              onChange={(e) => onChange({ description: e.target.value })}
              maxLength={PLAYTEST_RECRUITMENT_DESCRIPTION_MAX}
              rows={6}
              placeholder={'Help us shape the next build. Tap the button below to register and we’ll DM you a pairing code.'}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            />
          </FormField.Control>
          <FormField.HelpText>
            Discord markdown supported - bold, italics, links, mentions,
            line breaks. Up to {PLAYTEST_RECRUITMENT_DESCRIPTION_MAX} chars.
          </FormField.HelpText>
        </FormField>

        <FormField>
          <FormField.Label className={FIELD_LABEL_CLASS}>Button label</FormField.Label>
          <FormField.Control>
            <Input
              value={buttonLabel}
              onChange={(e) => onChange({ buttonLabel: e.target.value })}
              maxLength={PLAYTEST_RECRUITMENT_BUTTON_LABEL_MAX}
              placeholder={DEFAULT_PLAYTEST_RECRUITMENT_BUTTON_LABEL}
            />
          </FormField.Control>
        </FormField>

        <FormField>
          <FormField.Label className={FIELD_LABEL_CLASS}>
            Repost frequency
          </FormField.Label>
          <FormField.Control>
            <RadioCardGroup
              name="recruitment-repost-frequency"
              value={repostFrequency}
              onChange={(value) =>
                onChange({ repostFrequency: value as PlaytestRecruitmentFrequency })
              }
              options={REPOST_OPTIONS}
              orientation="grid"
              columns={4}
            />
          </FormField.Control>
          <FormField.HelpText>
            How often the bot reposts the recruitment message so it stays
            visible at the bottom of the channel.
          </FormField.HelpText>
        </FormField>

        <section className="flex flex-col gap-2">
          <span className={FIELD_LABEL_CLASS}>Channels</span>
          <DiscordChannelMultiSelect
            channels={channels}
            selectedIds={channelIds}
            onChange={(next) => onChange({ channelIds: next })}
            loading={channelsLoading}
            emptyMessage={
              hasConnection
                ? 'The bot has no readable channels in this server yet.'
                : 'Connect Discord to pick channels.'
            }
            disabled={!hasConnection}
          />
        </section>

      </Card.Content>
    </Card>
  );
}
